import { useEffect, useRef, useState } from "react";
import { ApiCall } from "../../../helper/axios";

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

const slugify = (name) =>
    String(name ?? "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

const mapCityToOption = (c) => ({
    value: slugify(c?.cityName),
    label: `${c?.cityName}, ${c?.countryName}`,
    cityId: c?.cityId,
    countryId: c?.countryId,
    cityName: c?.cityName,
    countryName: c?.countryName,
});

export const useCityStaticData = () => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState("");

    const debounceRef = useRef(null);
    const lastKeywordRef = useRef("");
    const cacheRef = useRef({});
    useEffect(() => {
        let mounted = true;
        const fetchCities = async () => {
            setLoading(true);
            try {
                const timeZone =
                    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Calcutta";
                const res = await ApiCall(
                    "POST",
                    `/excursions/static-data?timeZone=${encodeURIComponent(timeZone)}`,
                    {},
                    {}
                );
                if (!mounted) return;
                const list = res?.data?.data?.cities;
                if (Array.isArray(list)) {
                    setCities(list.map(mapCityToOption));
                }
            } catch (e) {
                if (mounted) setError(e?.message ?? "Failed to load cities");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchCities();
        return () => {
            mounted = false;
            clearTimeout(debounceRef.current);
        };
    }, []);

    const searchCities = (keyword) => {
        clearTimeout(debounceRef.current);
        const term = String(keyword ?? "").trim();
        lastKeywordRef.current = term;

        if (!term || term.length < MIN_SEARCH_LENGTH) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        const cacheKey = term.toLowerCase();
        if (cacheRef.current[cacheKey]) {
            setSearchResults(cacheRef.current[cacheKey]);
            setSearching(false);
            return;
        }

        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await ApiCall(
                    "POST",
                    `/excursions/static-data-search?search=${encodeURIComponent(term)}`,
                    {},
                    {}
                );
                if (lastKeywordRef.current !== term) return;
                const list = res?.data?.data?.cities;
                const mapped = Array.isArray(list) ? list.map(mapCityToOption) : [];
                cacheRef.current[cacheKey] = mapped;
                setSearchResults(mapped);
            } catch (e) {
                if (lastKeywordRef.current === term) setSearchResults([]);
            } finally {
                if (lastKeywordRef.current === term) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    };

    return { cities, loading, searchResults, searching, searchCities, error };
};
